// Daro options on the product page (blocks/daro-custom-options.liquid; scripts/options/PLAN.md section 5).
//   - show_if rules: hidden fields are disabled, so they don't post with the form
//   - required / length checks before add to cart (product-form.js calls validate())
//   - "Options +$X · Total $Y" under the fields, and the product price at the top includes the chosen extras
//   - getAddons(): the priced choices product-form.js adds as their own lines
//   - "variant" fields: the product's Shopify options, shown in the vendor's order; choosing one selects the same
//     value in the theme's variant picker (hidden while this block shows every option)
if (!customElements.get('daro-custom-options')) {
  customElements.define(
    'daro-custom-options',
    class DaroCustomOptions extends HTMLElement {
      connectedCallback() {
        this.variantPrice = Number(this.dataset.variantPrice || 0) / 100;
        this.money = new Intl.NumberFormat(undefined, { style: 'currency', currency: this.dataset.currency || 'USD' });
        this.addEventListener('change', (event) => {
          const variantField = event.target.closest('.daro-options__field--variant');
          if (variantField) this.selectVariant(variantField.querySelector('[data-option-name]').dataset.optionName, event.target.value);
          this.update();
        });
        this.addEventListener('input', () => this.update());
        this.root = this.closest('product-info') || document;
        // Every Shopify option has a field here: the theme's own picker steps aside (still does the work)
        const variantFields = this.querySelectorAll('.daro-options__field--variant [data-option-name]').length;
        const pickerOptions = this.root.querySelectorAll('variant-selects fieldset, variant-selects select').length;
        if (variantFields && variantFields >= pickerOptions) this.root.classList.add('daro-options-own-variants');
        // The theme re-renders the price on variant change: put the extras back on whatever it wrote
        const price = this.root.querySelector('product-price');
        if (price) new MutationObserver(() => this.applyTopPrice()).observe(price, { childList: true, subtree: true, characterData: true });
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const { sectionId, variant } = event.data || {};
            if (sectionId !== this.dataset.sectionId || !variant) return;
            this.variantPrice = variant.price / 100;
            this.update();
          });
        }
        this.update();
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      get fields() {
        return [...this.querySelectorAll('.daro-options__field')];
      }

      hasOptions() {
        return this.fields.length > 0;
      }

      /** One id per add to cart: ties the add-on lines to their line, and keeps configurations apart. */
      newGroup() {
        return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
      }

      /** Pick `value` for Shopify option `name` in the theme's variant picker, so it switches the variant. */
      selectVariant(name, value) {
        const picker = this.root.querySelector('variant-selects');
        if (!picker) return;
        const radio = [...picker.querySelectorAll('input[type="radio"]')].find((i) => i.name.trim().startsWith(`${name}-`) && i.value === value);
        if (radio) {
          if (!radio.checked) radio.click();
          return;
        }
        const select = [...picker.querySelectorAll('select')].find((sel) => (sel.closest('.product-form__input')?.querySelector('label, legend')?.textContent || sel.name).trim().startsWith(name));
        if (select && select.value !== value) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      /** The product price at the top: the variant's price (as the theme shows it) plus the chosen extras. */
      applyTopPrice() {
        const parse = (t) => Number(String(t).replace(/[^0-9.]/g, ''));
        for (const el of this.root.querySelectorAll('product-price .price-item')) {
          const shown = el.textContent.trim();
          if (!/\d/.test(shown)) continue;
          // The theme wrote a new price (variant change): that is the new base
          if (el.dataset.daroShown !== shown) el.dataset.daroBase = String(parse(shown));
          const text = this.money.format(Number(el.dataset.daroBase) + (this.extra || 0));
          el.dataset.daroShown = text;
          if (shown !== text) el.textContent = text;
        }
      }

      field(key) {
        return this.fields.find((f) => f.dataset.key === key);
      }

      /** The field's current answer as text ('' when nothing chosen or hidden). */
      valueOf(field) {
        if (!field || field.hidden) return '';
        const inputs = [...field.querySelectorAll('input, select, textarea')];
        switch (field.dataset.type) {
          case 'radio':
          case 'swatch':
          case 'variant':
            return inputs[0]?.tagName === 'SELECT' ? inputs[0].value : inputs.find((i) => i.checked)?.value || '';
          case 'checkbox':
            return inputs.filter((i) => i.checked).map((i) => (inputs.length > 1 ? i.closest('label').innerText.trim() : 'Yes')).join(', ');
          case 'file':
            return inputs[0]?.files?.length ? inputs[0].files[0].name : '';
          default:
            return (inputs[0]?.value || '').trim();
        }
      }

      visible(field) {
        const rules = field.dataset.showIf ? JSON.parse(field.dataset.showIf) : [];
        return rules.every((rule) => {
          const value = this.valueOf(this.field(rule.key));
          if (rule.op === 'filled') return value !== '';
          if (rule.op === 'neq') return value !== String(rule.value);
          if (rule.op === 'in') return [].concat(rule.value).map(String).includes(value);
          return value === String(rule.value);
        });
      }

      /** Inputs whose choice is currently selected (for prices and add-on lines). */
      chosen() {
        return this.fields
          .filter((f) => !f.hidden)
          .flatMap((f) => {
            const select = f.querySelector('select');
            if (select) return select.selectedOptions[0] ? [{ field: f, el: select.selectedOptions[0] }] : [];
            return [...f.querySelectorAll('input[type="radio"], input[type="checkbox"]')].filter((i) => i.checked).map((el) => ({ field: f, el }));
          });
      }

      update() {
        // Rules can chain (B shows for A, C for B): settle in a few passes
        for (let pass = 0; pass < 3; pass++) {
          for (const f of this.fields) {
            const show = this.visible(f);
            f.hidden = !show;
            f.querySelectorAll('input, select, textarea').forEach((i) => (i.disabled = !show));
          }
        }
        const extra = this.chosen().reduce((sum, c) => sum + (Number(c.el.dataset.price) || 0), 0);
        this.extra = extra;
        this.applyTopPrice();
        const total = this.querySelector('.daro-options__total');
        total.hidden = extra <= 0;
        this.querySelector('[data-daro-addons]').textContent = `+${this.money.format(extra)}`;
        this.querySelector('[data-daro-total]').textContent = this.money.format(this.variantPrice + extra);
      }

      getAddons() {
        const title = this.dataset.productTitle;
        return this.chosen()
          .filter((c) => Number(c.el.dataset.price) > 0 && c.el.dataset.variant)
          .map((c) => {
            const input = c.el.tagName === 'OPTION' ? c.el.closest('select') : c.el;
            return {
              id: c.el.dataset.variant,
              forTitle: title,
              // The main line's property this add-on stands for, and whether the buyer may drop it (checkboxes)
              option: (input.name.match(/^properties\[(.*)\]$/) || [])[1] || '',
              optional: c.field.dataset.type === 'checkbox' && !c.field.hasAttribute('data-required'),
            };
          });
      }

      validate() {
        this.update();
        let first = null;
        for (const f of this.fields) {
          const error = f.querySelector('.daro-options__error');
          let message = '';
          if (!f.hidden && f.hasAttribute('data-required') && this.valueOf(f) === '') {
            message = f.dataset.type === 'checkbox' ? 'Please tick this to continue.' : `Please choose ${f.dataset.label}.`;
          }
          error.textContent = message;
          error.hidden = !message;
          f.classList.toggle('daro-options__field--invalid', Boolean(message));
          if (message && !first) first = f;
        }
        if (first) {
          first.scrollIntoView({ behavior: 'smooth', block: 'center' });
          first.querySelector('input, select, textarea')?.focus({ preventScroll: true });
        }
        return !first;
      }
    }
  );
}
